import React from 'react';
import { Card, CardContent } from '../ui/card';
import { FileStack, AlertCircle, BadgeCheck, TrendingUp } from 'lucide-react';
import { formatCurrency } from '@/lib/formatters';

/**
 * Per-metric visual config. Keeping this in one place avoids four
 * near-duplicate card blocks and keeps accent colors consistent
 * across light/dark mode.
 */
const ACCENTS = {
  blue: {
    icon: 'text-blue-600 dark:text-blue-400',
    chip: 'bg-blue-50 dark:bg-blue-950/60',
    bar: 'from-blue-500/60 to-blue-500/0',
  },
  red: {
    icon: 'text-red-600 dark:text-red-400',
    chip: 'bg-red-50 dark:bg-red-950/60',
    bar: 'from-red-500/60 to-red-500/0',
  },
  green: {
    icon: 'text-green-600 dark:text-green-400',
    chip: 'bg-green-50 dark:bg-green-950/60',
    bar: 'from-green-500/60 to-green-500/0',
  },
  purple: {
    icon: 'text-purple-600 dark:text-purple-400',
    chip: 'bg-purple-50 dark:bg-purple-950/60',
    bar: 'from-purple-500/60 to-purple-500/0',
  },
};

function KpiTile({ label, value, icon: Icon, accent }) {
  const a = ACCENTS[accent] || ACCENTS.blue;
  return (
    <Card className="group relative overflow-hidden rounded-xl border shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      {/* Top accent strip, revealed on hover */}
      <div
        className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r ${a.bar} opacity-0 transition-opacity duration-200 group-hover:opacity-100`}
        aria-hidden="true"
      />
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <div className={`shrink-0 rounded-lg p-2 ${a.chip}`}>
            <Icon className={`h-4 w-4 ${a.icon}`} aria-hidden="true" />
          </div>
        </div>

        <div className="text-2xl font-semibold tracking-tight tabular-nums">
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

export function NewDashboardKPICards({ stats }) {
  const collectionRate = Number(stats?.collection_rate) || 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <KpiTile
        label="Total Disbursed"
        value={formatCurrency(stats?.total_disbursed || 0)}
        icon={FileStack}
        accent="blue"
      />
      <KpiTile
        label="Total Outstanding"
        value={formatCurrency(stats?.total_outstanding || 0)}
        icon={AlertCircle}
        accent="red"
      />
      <KpiTile
        label="Total Collected"
        value={formatCurrency(stats?.total_collected || 0)}
        icon={BadgeCheck}
        accent="green"
      />
      <KpiTile
        label="Collection Rate"
        value={`${collectionRate.toFixed(2)}%`}
        icon={TrendingUp}
        accent="purple"
      />
    </div>
  );
}
